import { useEffect, useMemo, useState } from 'react';
import { Modal } from 'react-bootstrap';
import * as microsoftTeams from '@microsoft/teams-js';
import { AZURE_CLIENT_ID, AZURE_AUTHORITY } from '../../environment';

// When the PRATUS Teams app is installed without administrator consent, SSO cannot retrieve the
// identity claims it needs (email, object ID, etc.). Microsoft Teams app submission requirements
// mandate a clear, actionable path forward -- so this dialog detects consent/configuration failures
// and presents admin-setup instructions instead of a generic error.
//
// PRATUS runs in Teams desktop/web, the Microsoft 365 app, and Outlook desktop/mobile. In some
// hosts (particularly Outlook and M365 hubs) an unconsented app receives a malformed or non-JWT
// value from the SSO token request rather than a standard Entra ID error code. These responses
// are treated as consent failures and routed to the admin-consent instructions.

// Normalize various error formats from 3 different apps + mobile clients into a string
function normalizeError(err) {
  try {
    if (!err) return '';
    if (typeof err === 'string') return err;
    if (typeof err.message === 'string' && err.message) return err.message;
    if (typeof err.errorMessage === 'string' && err.errorMessage)
      return err.errorMessage;
    // If message is empty, try to extract from stack
    if (typeof err.stack === 'string' && err.stack) {
      // Try to extract the first line of the stack that looks like an error message
      const firstLine = err.stack.split('\n')[0].trim();
      if (firstLine && firstLine.toLowerCase().includes('error')) {
        return firstLine;
      }
    }
    if (typeof err.toString === 'function') return err.toString();
    return JSON.stringify(err);
  } catch {
    return '';
  }
}

// Detect when the failure truly requires user/admin consent
function needsConsent(err) {
  try {
    const e = err ?? {};
    const msg = (
      typeof e === 'string' ? e : e.message || e.errorMessage || ''
    ).toLowerCase();

    // explicit suberrors / codes that indicate consent is needed
    if (
      e.suberror === 'consent_required' ||
      e.subError === 'consent_required' ||
      e.errorCode === 'interaction_required' ||
      e.server_error_code === 65001 ||
      msg.includes('consent_required') ||
      msg.includes('interaction_required')
    ) {
      return true;
    }

    // "consentish" tokens
    const tokens = [
      'consented',
      'aadsts500011',
      'incorrectconfiguration',
      'access denied for the resource',
      'invalid_resource',
      'unable to get access token', // Copilot/M365 error message for missing consent
    ];
    return tokens.some((t) => msg.includes(t.toLowerCase()));
  } catch {
    return false;
  }
}

export default function TeamsAppErrorDialog({ show, error }) {
  const [theme, setTheme] = useState('default');
  const [tenantId, setTenantId] = useState(null);

  // Initialize Teams and read context safely
  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        if (microsoftTeams?.app?.initialize) {
          await microsoftTeams.app.initialize();
          const ctx = await microsoftTeams.app.getContext();
          if (!mounted) return;
          const t = ctx?.app?.theme;
          setTheme(t === 'dark' || t === 'contrast' ? 'dark' : 'light');
          setTenantId(ctx?.user?.tenant?.id ?? null);
        }
      } catch (e) {
        console.debug('Teams init/context failed (expected in some hubs):', e);
      }
    })();
    return () => {
      mounted = false;
    };
  }, []);

  const message = useMemo(() => {
    const msg = normalizeError(error);
    return typeof msg === 'string' ? msg : '';
  }, [error]);

  const consentNeeded = useMemo(() => {
    // Primary: structured Azure/interaction signals
    if (needsConsent(error)) return true;

    // Fallback: M365 mobile apps sometimes swallow the real error entirely.
    // Only treat truly empty messages as consent-needed; [object Object] or short
    // strings are more likely downstream auth failures (e.g. 401) and should not
    // trigger the consent instructions.
    const normalizedMessage = (message || '').trim();
    if (normalizedMessage === '') {
      return true;
    }
    return false;
  }, [error, message]);

  const adminConsentUrl = useMemo(() => {
    if (!tenantId || !AZURE_CLIENT_ID) return null;
    const authority = AZURE_AUTHORITY || 'https://login.microsoftonline.com';
    const redirectUri = encodeURIComponent(window.location.origin);
    return `${authority}/${tenantId}/adminconsent?client_id=${AZURE_CLIENT_ID}&redirect_uri=${redirectUri}`;
  }, [tenantId]);

  useEffect(() => {
    if (message) {
      const label = consentNeeded ? 'consent error' : 'auth error';
      console.debug(`[TeamsAppErrorDialog] ${label}:`, message);
    }
  }, [message, consentNeeded]);

  return (
    <Modal show={show} centered backdrop="static" keyboard={false}>
      <Modal.Body
        className={`pb-5 ${theme === 'dark' ? 'text-white' : 'text-black'}`}
        style={{
          background: theme === 'dark' ? 'transparent' : 'white',
          color: theme === 'dark' ? 'white' : 'black',
        }}
      >
        <div className="d-flex flex-column align-items-center">
          {consentNeeded ? (
            <>
              <p>
                Thank you for choosing Disaster Tech&apos;s PRATUS to improve
                your crisis management and exercise training capabilities.
              </p>
              <p>
                <strong>⚠️ Admin Consent Required</strong>: Before you can fully
                use PRATUS, an administrator must install the app in your
                organization&apos;s app catalog and grant the required
                permissions for all users.
              </p>
              {adminConsentUrl ? (
                <p>
                  Please ask your administrator to{' '}
                  <a
                    href={adminConsentUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    grant admin consent
                  </a>{' '}
                  to complete the setup.
                </p>
              ) : (
                <p>
                  Please contact your administrator to grant consent for this
                  application.
                </p>
              )}
              <p>
                After these steps are complete, please uninstall and re-add the
                app in your personal scope.
              </p>
              <p className="mt-2 small">
                <strong>Support:</strong>{' '}
                <a
                  href="https://www.disastertech.com/knowledge/kb-tickets/new"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Submit a support ticket
                </a>
              </p>
            </>
          ) : (
            <>
              <p>
                We’re sorry, PRATUS is having trouble authenticating with your
                Microsoft account.
              </p>
            </>
          )}
        </div>
      </Modal.Body>
    </Modal>
  );
}
